/**
 * Student Profile Constants
 *
 * Centralized constants for student profile forms and validation.
 * Used across frontend forms and backend LLM tools.
 */

import { z } from 'zod';
import { COUNTRY_NAMES } from './countryInformation.js';

// ============================================================================
// TIME-RELATED CONSTANTS
// ============================================================================

export const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
] as const;

export const GRADUATING_YEARS = Array.from({ length: 2029 - 2000 + 1 }, (_, i) => 2000 + i);

// ============================================================================
// CURRICULUM / ACADEMIC SYSTEMS
// ============================================================================


export const RECOGNIZED_DEGREE_VALUES = [
  "International Baccalaureate",
  "A-Levels",
  "Bachillerato Mexicano",
  "Certificado de Educación Secundaria Común Peruano",
  "Bachillerato Colombiano",
  "Licencia de Educación Chilena",
  "Bachillerato Argentino",
  "Bachillerato Ecuatoriano",
  "Certificado de Ensino Médio",
  "Bachillerato Venezolano",
  "Bachillerato Boliviano",
  "Bachillerato de Costa Rica",
  "Bachillerato Dominicano",
  "Educación Media Uruguay",
  "Spanish Bachillerato",
  "Advanced Placement",
  "Standardized Testing",
  "Other",
] as const;
export const CURRICULUM_ENUM = z.enum(RECOGNIZED_DEGREE_VALUES);

export const RECOGNIZED_DEGREE_COUNTRY_CODE: Readonly<Record<Curriculum, string>> = {
  "International Baccalaureate": "GLOBAL",
  "A-Levels": "GB",
  "Bachillerato Mexicano": "MX",
  "Certificado de Educación Secundaria Común Peruano": "PE",
  "Bachillerato Colombiano": "CO",
  "Licencia de Educación Chilena": "CL",
  "Bachillerato Argentino": "AR",
  "Bachillerato Ecuatoriano": "EC",
  "Certificado de Ensino Médio": "BR",
  "Bachillerato Venezolano": "VE",
  "Bachillerato Boliviano": "BO",
  "Bachillerato de Costa Rica": "CR",
  "Bachillerato Dominicano": "DO",
  "Educación Media Uruguay": "UY",
  "Spanish Bachillerato": "ES",
  "Advanced Placement": "US",
  "Standardized Testing": "GLOBAL",
  "Other": "GLOBAL",
} as const;
export type Curriculum = z.infer<typeof CURRICULUM_ENUM>;

// Alias for backward compatibility with LLM tools
export const CURRICULUM_LABELS = RECOGNIZED_DEGREE_VALUES;

export const hasValidCurricula = (curricula?: Curriculum[]): boolean => Boolean(curricula && curricula.length > 0 && (curricula.length > 1 || curricula[0] !== 'Standardized Testing'));
export const hasDirectEntryCurriculum = (curricula?: Curriculum[]): boolean => Boolean(curricula && curricula.length > 0 && curricula.some(curriculum => DIRECT_ENTRY_CURRICULUM_LABELS.includes(curriculum)));
export const hasNonDirectEntryCurriculum = (curricula?: Curriculum[]): boolean => Boolean(curricula && curricula.length > 0 && curricula.some(curriculum => !DIRECT_ENTRY_CURRICULUM_LABELS.includes(curriculum) && curriculum !== 'Standardized Testing'));

// Curriculum types that allow direct entry (no foundation year required)
export const DIRECT_ENTRY_CURRICULUM_LABELS: readonly Curriculum[] = ['A-Levels', 'International Baccalaureate', 'Spanish Bachillerato', 'Advanced Placement'];
export const DIRECT_ENTRY_CURRICULUM = ['GB', 'GLOBAL', 'ES', 'US'] as const;

// ============================================================================
// UNIVERSITY INTEREST
// ============================================================================

export const START_DATE_OPTIONS = [
  { value: '2026-09-01', label: 'Fall 2026' },
  { value: '2027-09-01', label: 'Fall 2027' },
  { value: '2028-09-01', label: 'Fall 2028' },
  { value: '2029-09-01', label: 'Fall 2029' },
  { value: '2030-09-01', label: 'Fall 2030' },
] as const;

export const UNIVERSITY_MAJOR_OPTIONS = [
  'Computer Science',
  'Engineering',
  'Business',
  'Medicine',
  'Law',
  'Arts and Humanities',
  'Social Sciences',
  'Natural Sciences',
  'Mathematics',
  'Architecture',
  'Education',
  'Psychology',
  'Economics',
  'Finance',
  'Marketing',
  'Political Science',
  'Environmental Science',
  'Media Studies',
  'Undecided'
] as const;

// Countries to study in (subset of all countries)
export const STUDY_COUNTRY_OPTIONS = [
  { value: 'GB', label: 'United Kingdom' },
  { value: 'US', label: 'United States' },
  { value: 'ES', label: 'Spain' },
  { value: 'CA', label: 'Canada' },
  { value: 'AU', label: 'Australia' },
  { value: 'NL', label: 'Netherlands' },
  { value: 'FR', label: 'France' },
  { value: 'DE', label: 'Germany' },
  { value: 'IT', label: 'Italy' },
  { value: 'MX', label: 'Mexico' },
  { value: 'BR', label: 'Brazil' },
  { value: 'NZ', label: 'New Zealand' },
  { value: 'SG', label: 'Singapore' },
  { value: 'HK', label: 'Hong Kong' },
  { value: 'IE', label: 'Ireland' },
] as const;

// Study country codes only (for validation)
export const STUDY_COUNTRY_CODES = STUDY_COUNTRY_OPTIONS.map(c => c.value);

// ============================================================================
// FAMILY BACKGROUND
// ============================================================================

export const FAMILY_EDUCATION_OPTIONS = [
  'No formal education',
  'Some high school',
  'High school graduate',
  'Some college',
  'Associate degree',
  "Bachelor's degree",
  "Master's degree",
  'Doctoral degree',
  'Professional degree (MD, JD, etc.)',
  'Unknown'
] as const;

export const PARENT_INCOME_LEVELS = [
  'Less than $25,000',
  '$25,000 - $50,000',
  '$50,000 - $75,000',
  '$75,000 - $100,000',
  '$100,000 - $150,000',
  '$150,000 - $200,000',
  'More than $200,000',
  'Prefer not to say'
] as const;

export const DISABILITY_OPTIONS = [
  { value: 'None', label: 'None / Prefer not to say' },
  { value: 'Visual impairment', label: 'Visual impairment' },
  { value: 'Hearing impairment', label: 'Hearing impairment' },
  { value: 'Mobility impairment', label: 'Mobility impairment' },
  { value: 'Learning disability', label: 'Learning disability' },
  { value: 'Attention deficit disorder', label: 'Attention deficit disorder' },
  { value: 'Autism spectrum disorder', label: 'Autism spectrum disorder' },
  { value: 'Chronic illness', label: 'Chronic illness' },
  { value: 'Other', label: 'Other' }
] as const;

// Disability values only (for validation)
export const DISABILITY_VALUES = DISABILITY_OPTIONS.map(d => d.value);

export const FIRST_GENERATION_OPTIONS = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' }
] as const;

// ============================================================================
// HELP NEEDED
// ============================================================================

export const HELP_NEEDED_OPTIONS = [
  {
    value: 'Country-specific guidance',
    label: '🌍 Understanding country-specific application processes'
  },
  {
    value: 'University matching',
    label: '🎯 Finding universities and courses that match my profile'
  },
  {
    value: 'Essay writing',
    label: '✍️ Writing application essays and personal statements'
  },
  {
    value: 'Scholarship search',
    label: '💰 Finding and applying for scholarships'
  }
] as const;

// Help needed values only (for validation)
export const HELP_NEEDED_VALUES = HELP_NEEDED_OPTIONS.map(h => h.value);

// ============================================================================
// LANGUAGE PROFICIENCY
// ============================================================================

export const LANGUAGE_PROFICIENCY_LEVELS = [
  'Native',
  'Fluent',
  'Intermediate',
  'Beginner'
] as const;

// ============================================================================
// COUNTRY-RELATED (Re-exported from countryInformation)
// ============================================================================

// All country codes (ISO 3166-1 alpha-2)
export const COUNTRY_CODES = Object.keys(COUNTRY_NAMES) as Array<keyof typeof COUNTRY_NAMES>;

// Country options for select dropdowns
export const COUNTRY_OPTIONS = Object.entries(COUNTRY_NAMES).map(([code, name]) => ({
  value: code,
  label: name
}));

// Re-export COUNTRY_NAMES for convenience
export { COUNTRY_NAMES };
